"use client";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { Box, IconButton } from "@mui/material";
import { useTheme } from "../theme/ThemeContext";

export default function ThemeToggle() {
  const { isDarkMode, toggleTheme } = useTheme();

  // 定义一些常量，方便精确控制 - 减小宽度
  const TOGGLE_WIDTH = "3.5rem"; // 从 "4rem" 减小到 "3.5rem"
  const TOGGLE_HEIGHT = "2rem";
  const ICON_SIZE = "1.5rem"; // 稍微减小图标尺寸
  const ICON_FONT_SIZE = "1.1rem"; // 减小图标字体大小
  const BG_SIZE = "1.5rem"; // 稍微减小背景圈尺寸
  const HORIZONTAL_PADDING = "0.2rem"; // 减小水平内边距

  // 计算背景位置，确保精确对齐
  const bgLeftLight = `calc(${TOGGLE_WIDTH} - ${BG_SIZE} - ${HORIZONTAL_PADDING})`;

  // 添加条件切换函数
  const handleThemeToggle = (targetMode: boolean) => {
    // 只有当目标模式与当前模式不同时才切换
    if (isDarkMode !== targetMode) {
      toggleTheme();
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: "2rem", // 更平滑的边角
        width: TOGGLE_WIDTH,
        height: TOGGLE_HEIGHT,
        position: "relative",
        overflow: "hidden",

        px: HORIZONTAL_PADDING,
        boxSizing: "border-box", // 确保边距计算准确
      }}>
      {/* 背景指示器 */}
      <Box
        sx={{
          position: "absolute",
          width: BG_SIZE,
          height: BG_SIZE,
          borderRadius: "50%",
          bgcolor: "primary.main",
          left: isDarkMode ? HORIZONTAL_PADDING : bgLeftLight,
          // 精确垂直居中
          top: "50%",
          transform: "translateY(-50%)",
          transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // 更平滑的过渡动画
          zIndex: 0,
        }}
      />

      <Box sx={{ display: "flex", alignItems: "center", height: "100%", justifyContent: "space-between" }}>
        {/* 暗模式图标 */}
        <IconButton
          onClick={() => handleThemeToggle(true)} // 只有当前不是暗模式时才切换
          disableRipple // 移除点击波纹效果，使视觉更干净
          sx={{
            p: 0,
            height: ICON_SIZE,
            width: ICON_SIZE,
            zIndex: 1,
            position: "relative",
            transition: "transform 0.2s",
            transform: isDarkMode ? "scale(1.05)" : "scale(1)", // 轻微的缩放效果
            textAlign: "center", // 确保图标居中
          }}>
          <DarkModeOutlinedIcon
            sx={{
              fontSize: ICON_FONT_SIZE,
              color: isDarkMode ? "#fff" : "text.secondary",
              transition: "color 0.3s",
            }}
          />
        </IconButton>

        {/* 亮模式图标 */}
        <IconButton
          onClick={() => handleThemeToggle(false)} // 只有当前不是亮模式时才切换
          disableRipple // 移除点击波纹效果
          sx={{
            p: 0,
            height: ICON_SIZE,
            width: ICON_SIZE,
            zIndex: 1,
            position: "relative",
            transition: "transform 0.2s",
            transform: !isDarkMode ? "scale(1.05)" : "scale(1)", // 轻微的缩放效果
            textAlign: "center",
          }}>
          <LightModeOutlinedIcon
            sx={{
              fontSize: ICON_FONT_SIZE,
              color: !isDarkMode ? "#fff" : "text.secondary",
              transition: "color 0.3s",
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
}
